import { useState, useRef, useCallback, useEffect, ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface TooltipProps {
    title: ReactNode
    children: ReactNode
    placement?: 'top' | 'bottom' | 'left' | 'right'
    arrow?: boolean
    className?: string
    // 'dark' (default) is the existing solid slate-800/white-text bubble used everywhere.
    // 'light' mirrors the white/outlined MUI Joy tooltip style (e.g. Ongoing Loads' rate-cell
    // breakdown) for callers that need dark text to stay readable in light mode.
    variant?: 'dark' | 'light'
}

const ARROW_CLASSES: Record<string, string> = {
    top: '-bottom-1 left-1/2 -translate-x-1/2',
    bottom: '-top-1 left-1/2 -translate-x-1/2',
    left: '-right-1 top-1/2 -translate-y-1/2',
    right: '-left-1 top-1/2 -translate-y-1/2',
}

export const Tooltip = ({ title, children, placement = 'top', arrow = false, className = '', variant = 'dark' }: TooltipProps) => {
    const [visible, setVisible] = useState(false)
    const [position, setPosition] = useState({ top: 0, left: 0 })
    const triggerRef = useRef<HTMLSpanElement>(null)
    const tooltipRef = useRef<HTMLDivElement>(null)
    const timerRef = useRef<ReturnType<typeof setTimeout>>()

    const updatePosition = useCallback(() => {
        if (!triggerRef.current) return
        const rect = triggerRef.current.getBoundingClientRect()
        const tw = tooltipRef.current?.offsetWidth ?? 0
        const th = tooltipRef.current?.offsetHeight ?? 0

        let top = 0
        let left = 0
        switch (placement) {
            case 'top':
                top = rect.top - th - 8
                left = rect.left + rect.width / 2 - tw / 2
                break
            case 'bottom':
                top = rect.bottom + 8
                left = rect.left + rect.width / 2 - tw / 2
                break
            case 'left':
                top = rect.top + rect.height / 2 - th / 2
                left = rect.left - tw - 8
                break
            case 'right':
                top = rect.top + rect.height / 2 - th / 2
                left = rect.right + 8
                break
        }

        left = Math.max(8, Math.min(left, window.innerWidth - tw - 8))
        top = Math.max(8, Math.min(top, window.innerHeight - th - 8))

        setPosition({ top, left })
    }, [placement])

    const show = useCallback(() => {
        timerRef.current = setTimeout(() => setVisible(true), 200)
    }, [])

    const hide = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current)
        setVisible(false)
    }, [])

    useEffect(() => {
        if (visible) updatePosition()
    }, [visible, updatePosition])

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current)
        }
    }, [])

    if (!title) return <>{children}</>

    // The light bubble is on the v2 semantic tokens (`surface`/`foreground`/`border`), which carry their own
    // dark-mode values — no paired `dark:` class per rule. The `dark` variant below is still raw slate so the
    // existing callers keep the exact bubble they have today.
    const bubbleClasses = variant === 'light' ? 'bg-surface text-foreground border border-border' : 'bg-slate-800 text-white'
    const arrowClasses = variant === 'light' ? 'bg-surface border-r border-b border-border' : 'bg-slate-800'

    return (
        <>
            <span ref={triggerRef} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide} className="inline-flex min-w-0">
                {children}
            </span>
            {visible &&
                createPortal(
                    <div
                        ref={tooltipRef}
                        className={`fixed z-[10000] pointer-events-none max-w-xs rounded-md px-2.5 py-1.5 text-xs shadow-lg ${bubbleClasses} ${className}`}
                        style={{ top: position.top, left: position.left }}
                        role="tooltip"
                    >
                        {title}
                        {arrow && <div className={`absolute h-2 w-2 rotate-45 ${arrowClasses} ${ARROW_CLASSES[placement]}`} />}
                    </div>,
                    document.body
                )}
        </>
    )
}
